import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output,
    SecurityContext
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DeviceBrowser, DeviceDetectService, UrlService } from '@draeger-academy/common';
import {
    DaProgressiveImageInterface,
    DaProgressiveImageOptionsInterface
} from './shared/interfaces/da-proressive-image.interface';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';

@Component({
    selector: 'da-progressive-image',
    templateUrl: './da-progressive-image.component.html',
    styleUrls: ['./da-progressive-image.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DaProgressiveImageComponent implements OnInit, OnDestroy {
    @Output() loaded: EventEmitter<boolean> = new EventEmitter();
    public activeIndex$: BehaviorSubject<number> = new BehaviorSubject(undefined);
    // _akamaiImageSizes = [130, 180, 200, 320, 380, 400, 420, 480, 600, 768, 780, 960, 1120, 1280, 1400, 1920];
    private readonly _offset = 200;
    public offset = this._offset;
    private readonly _draegerCrawlerUserAgent = 'draeger-crawler';
    private _isDraegerCrawler: boolean;
    private _resizeTimeout: any;
    private _activeImageSubscription: Subscription;
    private _showImageSubscription: Subscription;
    private _placeHolderImageTemplate =
        // prettier-ignore
        // tslint:disable-next-line:max-line-length
        'data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 WIDTH HEIGHT\' width=\'WIDTH\' height=\'HEIGHT\'%3E%3Crect fill=\'white\' width=\'WIDTH\' height=\'HEIGHT\' /%3E%3C/svg%3E';
    private _$showImage: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _$currentImage: BehaviorSubject<SafeResourceUrl> = new BehaviorSubject(null);

    constructor(private _domSanitizer: DomSanitizer, private _urlService: UrlService, private _deviceDetectService: DeviceDetectService) {
    }

    private _options: DaProgressiveImageOptionsInterface;

    public get options(): DaProgressiveImageOptionsInterface {
        return this._options;
    }

    @Input()
    public set options(value: DaProgressiveImageOptionsInterface) {
        const _value = {...value};
        _value.aspectRatios = [..._value.aspectRatios];

        // sort breakpoint DESC
        _value.aspectRatios.sort((_a, _b) => _b.breakpoint - _a.breakpoint);

        // calculate minWidth for Crawler
        _value.aspectRatios.map((_item, _index, _itemArray) => {
            const _nextIndex = _index + 1 < _itemArray.length ? _index + 1 : _index;
            _item.minWidth = _index !== _nextIndex ? _itemArray[_nextIndex].breakpoint + 1 : 0;
        });

        // set default config for lazy load
        _value.lazyLoadOptions = _value.lazyLoadOptions || {};
        _value.lazyLoadOptions = {...{rootMargin: `${this._offset}px`}, ...value.lazyLoadOptions};

        // find active ratio and set index
        this._setActiveRatio(window.innerWidth, _value);

        this._options = _value;
    }

    public get sourceImages(): DaProgressiveImageInterface[] {
        return this.options.aspectRatios.slice(0, -1);
    }

    public get activeImage(): DaProgressiveImageInterface {
        return this.options.aspectRatios[this.activeIndex$.getValue()];
    }

    public get defaultImage(): DaProgressiveImageInterface {
        return this.options.aspectRatios.slice(-1).pop();
    }

    public get isCrawler(): boolean {
        return this._isDraegerCrawler;
    }

    public get showImage$(): Observable<boolean> {
        return this._$showImage.asObservable();
    }

    public get showImage(): boolean {
        return this._$showImage.value;
    }

    public get currentImage$(): Observable<string | SafeResourceUrl> {
        return this._$currentImage.asObservable();
    }

    public get intersectionObserverActive(): boolean {
        return (
            'IntersectionObserver' in window &&
            'IntersectionObserverEntry' in window &&
            'isIntersecting' in IntersectionObserverEntry.prototype
        );
    }

    @HostListener('window:resize', ['$event'])
    onResize(event) {
        if (!this.isCrawler) {
            clearTimeout(this._resizeTimeout);
            this._resizeTimeout = setTimeout(() => {
                this._setActiveRatio(event.target.innerWidth);
            }, 500);
        }
    }

    public minWidthMedia(value: number): string {
        return `(min-width: ${value}px)`;
    }

    public sourceSetDensity(value: DaProgressiveImageInterface, densityDeep: number, densityStart = 1): string {
        let _sourceSet = '';
        for (; densityStart <= densityDeep; densityStart++) {
            _sourceSet += _sourceSet !== '' ? `, ` : ``;
            _sourceSet += `${value.akamaiImage}?imwidth=${value.width * densityStart}`;
            _sourceSet += densityStart !== 1 ? ` ${densityStart}x` : ``;
        }
        return _sourceSet;
    }

    public intersectionObserver(event?: boolean) {
        const value = event != null && typeof event === 'boolean' ? event : true;
        this._$showImage.next(value);
    }

    public getCurrentImageSrc(width: number, height: number, ratioObject?: DaProgressiveImageInterface): SafeResourceUrl {
        ratioObject = ratioObject || this.activeImage;
        let _url: string;

        _url = this._placeHolderImageTemplate.replace(/HEIGHT/g, height.toString()).replace(/WIDTH/g, width.toString());

        if (
            (this.isCrawler || !this.intersectionObserverActive || (!this.isCrawler && this.showImage)) &&
            ratioObject.akamaiImage.trim() !== ''
        ) {
            _url = this._urlService.relativeImageUrl(ratioObject.akamaiImage) + '?imwidth=' + width;
        }

        return this._domSanitizer.bypassSecurityTrustResourceUrl(_url);
    }

    public ngOnInit(): void {
        this._isDraegerCrawler = window && window.navigator.userAgent.indexOf(this._draegerCrawlerUserAgent) > -1;
        if (!this.isCrawler && this._activeImageSubscription == null) {
            this._activeImageSubscription = this.activeIndex$.pipe(distinctUntilChanged()).subscribe((value) => {
                this.setCurrentImage();
            });
            this._showImageSubscription = this.showImage$.pipe(filter((value) => value === true)).subscribe(() => {
                this._setActiveRatio(window.innerWidth);
                if (this._showImageSubscription) {
                    this._showImageSubscription.unsubscribe();
                }
            });
            this._$showImage.next(!this.intersectionObserverActive);
        }
    }

    public ngOnDestroy(): void {
        if (this._activeImageSubscription) {
            this._activeImageSubscription.unsubscribe();
        }
        if (this._showImageSubscription) {
            this._showImageSubscription.unsubscribe();
        }
    }

    private _setActiveRatio(clientWidth: number, options?: DaProgressiveImageOptionsInterface): void {
        let _newActiveIndex;
        options = options || this.options;

        if (options.aspectRatios.length > 1 && !this.isCrawler) {
            for (const [index, ratio] of options.aspectRatios.entries()) {
                if (clientWidth >= ratio.breakpoint) {
                    _newActiveIndex = index;
                    break;
                }
            }
        }

        if (typeof _newActiveIndex === 'undefined') {
            _newActiveIndex = options.aspectRatios.length - 1;
        }
        if (this.activeIndex$.getValue() !== _newActiveIndex && this.showImage) {
            this.activeIndex$.next(_newActiveIndex);
        } else if (!this.showImage && this._$currentImage.getValue() == null) {
            const _ratio = options.aspectRatios[_newActiveIndex];
            const _image = this.getCurrentImageSrc(_ratio.width, _ratio.height, _ratio);
            this._$currentImage.next(_image);
        }
    }

    private setCurrentImage(): void {
        if (typeof this.activeIndex$.getValue() === 'number') {
            const _image = this.getCurrentImageSrc(this.activeImage.width, this.activeImage.height);
            const _imageSrc = this._domSanitizer.sanitize(SecurityContext.RESOURCE_URL, _image);
            const _imageSrcOld = this._domSanitizer.sanitize(SecurityContext.RESOURCE_URL, this._$currentImage.getValue()) || '';
            this.loaded.emit(true);
            if (_imageSrc !== _imageSrcOld) {
                if (_imageSrc.indexOf('data:image/svg+xml') === -1) {
                    const _imageObject = new Image();
                    _imageObject.src = _imageSrc;
                }
                this._$currentImage.next(_image);
                this.loaded.emit(true);
            } else if (this._deviceDetectService.isBrowser(DeviceBrowser.IE)) {
                this.loaded.emit(true);
            }
        }
    }
}
